import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, chapterPages, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHead from '@/components/feature/PageHead';
import ComicCard from '@/components/feature/ComicCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));
  const [loadedPages, setLoadedPages] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(3);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState<'read' | 'info'>('info');
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (comic && viewMode === 'read') {
      setLoadedPages(chapterPages.slice(0, 3));
      setPageIndex(3);
      setHasMore(true);
    }
  }, [comic, viewMode]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    setLoading(true);
    setTimeout(() => {
      const nextPages = chapterPages.slice(pageIndex, pageIndex + 3);
      if (nextPages.length === 0) {
        setHasMore(false);
      } else {
        setLoadedPages((prev) => [...prev, ...nextPages]);
        setPageIndex((prev) => prev + 3);
      }
      setLoading(false);
    }, 800);
  }, [loading, hasMore, pageIndex]);

  useEffect(() => {
    if (viewMode !== 'read') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [loadMore, hasMore, loading, viewMode]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex flex-col items-center justify-center h-screen">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    genre: comic.tags,
    url: `https://www.tianyoucomics.com/comic/${comic.id}`,
    image: comic.cover,
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `《${comic.title}》是否免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的！甜柚漫画所有漫画均为免费阅读，包括《' + comic.title + '》，全集免费畅享，无需付费解锁。',
        },
      },
      {
        '@type': 'Question',
        name: `《${comic.title}》支持竖屏下拉式阅读吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '支持！甜柚漫画全站采用竖屏下拉式阅读模式，手机下滑即可顺畅阅读，无限滚动加载，无需翻页。',
        },
      },
      {
        '@type': 'Question',
        name: `《${comic.title}》的更新频率如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》${comic.status === '连载中' ? '正在持续更新中，通常保持高频更新节奏' : '已完结，所有章节均可一次性畅快阅读'}。`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <PageHead
        title={`${comic.title} - 甜柚漫画 | 免费下拉式阅读`}
        description={`${comic.title}，${comic.author}作品，${comic.description}支持下拉式阅读、无限滚动，免费全集漫画尽在甜柚漫画。`}
        keywords={`${comic.title},${comic.category}漫画,下拉式阅读,免费漫画,甜柚漫画,${comic.tags.join(',')}`}
        canonicalPath={`/comic/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: `${comic.category}漫画`, path: `/category/${categories.find((c) => c.name === comic.category)?.slug || 'all'}` },
                { label: comic.title },
              ]}
            />
          </div>

          {viewMode === 'info' ? (
            <>
              {/* Comic Info */}
              <section className="py-6 md:py-10">
                <div className="px-4 md:px-6 max-w-7xl mx-auto">
                  <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                    <div className="flex-shrink-0 w-full md:w-[280px]">
                      <div className="aspect-[5/7] rounded-lg overflow-hidden bg-background-100">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top"
                          width="400"
                          height="560"
                        />
                      </div>
                    </div>

                    <div className="flex-1">
                      <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                        {comic.title}
                      </h1>
                      <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          comic.status === '连载中'
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-foreground-100 text-foreground-700'
                        }`}>
                          {comic.status}
                        </span>
                        <span className="text-sm text-foreground-600">
                          <i className="ri-eye-line mr-1"></i>{comic.views}
                        </span>
                        <span className="text-sm text-foreground-600">
                          <i className="ri-file-list-3-line mr-1"></i>{comic.chapters}话
                        </span>
                        <span className="flex items-center gap-1 text-sm text-accent-500 font-semibold">
                          <i className="ri-star-fill"></i>{comic.rating}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {comic.tags.map((tag) => (
                          <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                        {comic.description}
                      </p>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => setViewMode('read')}
                          className="px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                        >
                          <i className="ri-play-circle-line text-lg"></i>
                          开始阅读
                        </button>
                        <Link
                          to={`/category/${categories.find((c) => c.name === comic.category)?.slug || 'all'}`}
                          className="px-8 py-3 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                        >
                          <i className="ri-arrow-left-line"></i>
                          返回分类
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Related Comics */}
              {relatedComics.length > 0 && (
                <section className="py-10 md:py-14 bg-background-100">
                  <div className="px-4 md:px-6 max-w-7xl mx-auto">
                    <h2 className="text-xl font-heading text-foreground-900 mb-6">
                      相关推荐
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                      {relatedComics.map((c) => (
                        <ComicCard key={c.id} {...c} />
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </>
          ) : (
            <>
              {/* Reading Mode */}
              <section className="py-4 md:py-6">
                <div className="px-4 md:px-6 max-w-7xl mx-auto">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => setViewMode('info')}
                      className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      返回详情
                    </button>
                    <h2 className="text-sm font-semibold text-foreground-900">{comic.title}</h2>
                    <span className="text-xs text-foreground-500">
                      第1话
                    </span>
                  </div>

                  <div className="max-w-[480px] mx-auto flex flex-col gap-0">
                    {loadedPages.map((page, index) => (
                      <div key={index} className="w-full bg-background-100">
                        <img
                          src={page}
                          alt={`${comic.title} 第${index + 1}页`}
                          className="w-full h-auto"
                          width="800"
                          height="1200"
                        />
                      </div>
                    ))}

                    <div ref={loaderRef} className="py-8 text-center">
                      {loading && (
                        <div className="flex items-center justify-center gap-2 text-sm text-foreground-500">
                          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                          加载中...
                        </div>
                      )}
                      {!loading && hasMore && (
                        <button
                          onClick={loadMore}
                          className="px-6 py-2.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 text-sm transition-colors cursor-pointer whitespace-nowrap"
                        >
                          点击加载更多
                        </button>
                      )}
                      {!hasMore && (
                        <div className="text-sm text-foreground-400">
                          <i className="ri-check-double-line mr-1"></i>
                          已加载全部内容
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}